import { ClientSetup } from '@/components/mcp/ClientSetup';
import {
  Bullets,
  Code,
  HelpSection,
  Note,
  P,
  Screen,
  SeeAlso,
  Steps,
  Strong,
  Terms,
} from '../prose';

/**
 * MCP のつなぎ方。
 *
 * クライアント別の設定は `ClientSetup` をそのまま使う。手順書を 2 箇所に持つと、
 * 各社の設定 UI が変わったときに必ず片方だけ古くなる。
 * ここは公開ページなので実物のトークンは差し込まない (token に null を渡すと
 * プレースホルダ表示になる)。
 */
export function McpSetupArticle() {
  const endpoint = `${window.location.origin}/mcp`;

  return (
    <>
      <HelpSection heading="つなぐと何ができるか">
        <P>
          {
            'AI クライアントから INOU の MCP サーバーに接続すると、作業の会話の流れで工数を記録できます。記録も参照も'
          }
          <Strong>自分の分だけ</Strong>
          {'で、管理者のトークンでも他の人の記録には届きません。'}
        </P>
        <P>{'使えるのは次の 6 つです。'}</P>
        <Terms
          rows={[
            { term: 'list_projects', value: '記録先に指定できるプロジェクトの一覧' },
            { term: 'list_my_assignments', value: '自分の当月のアサインと消化状況' },
            { term: 'log_time', value: '自分の工数を記録する' },
            { term: 'list_my_time_logs', value: '自分が記録した工数を期間で取り出す' },
            { term: 'update_time_log', value: '自分の記録を訂正する' },
            { term: 'delete_time_log', value: '自分の記録を削除する' },
          ]}
        />
      </HelpSection>

      <HelpSection heading="接続の方法は 2 つ">
        <P>
          {'おすすめは'}
          <Strong>ブラウザで許可する方法（OAuth）</Strong>
          {
            'です。接続先の URL を登録するだけで、トークンを控える手間も、設定ファイルに秘密が残ることもありません。クライアントが対応していないときだけ、トークンを発行して貼り付けます。'
          }
        </P>
        <Terms
          rows={[
            {
              term: 'ブラウザで許可',
              value:
                '接続先の URL だけを登録します。初回の接続でブラウザが開き、ふだんのログインで許可を押すと繋がります。許可した相手はあとから「接続済みアプリ」で解除できます。',
            },
            {
              term: 'トークンを貼る',
              value:
                '画面でトークンを発行し、クライアントの設定に書きます。平文が手元に残るので、端末ごとに分けて発行し、使わなくなったら失効させてください。',
            },
          ]}
        />
      </HelpSection>

      <HelpSection heading="接続の手順（ブラウザで許可）">
        <Steps
          items={[
            {
              title: '接続先を確認する',
              body: (
                <P>
                  {'接続先は '}
                  <Code>{endpoint}</Code>
                  {' です。'}
                  <Screen to="/settings/mcp">MCP接続</Screen>
                  {' の「接続先」からもコピーできます。'}
                </P>
              ),
            },
            {
              title: 'AI クライアントに登録する',
              body: (
                <P>
                  {
                    '使っているクライアントのタブを選び、「ブラウザで許可」の内容を貼り付けます。書くのは URL だけです。'
                  }
                </P>
              ),
            },
            {
              title: 'ブラウザで許可する',
              body: (
                <>
                  <P>
                    {
                      '初回の接続でブラウザが開きます。ログインしていなければログインし、内容を確かめて「接続を許可」を押してください。'
                    }
                  </P>
                  <Note label="許可するのは自分の分だけ">
                    <P>
                      {
                        '許可したアプリができるのは、あなた自身の工数の記録と参照だけです。他の人の記録や全社の集計には届きません。'
                      }
                    </P>
                  </Note>
                </>
              ),
            },
            {
              title: '接続を確認する',
              body: (
                <P>
                  {
                    'AI に「INOU で自分の今月のアサインを見せて」と頼みます。アサインが返ってくれば接続できています。'
                  }
                </P>
              ),
            },
          ]}
        />
      </HelpSection>

      <HelpSection heading="接続の手順（トークンを貼る）">
        <Steps
          items={[
            {
              title: 'トークンを発行する',
              body: (
                <>
                  <P>
                    <Screen to="/settings/mcp">MCP接続</Screen>
                    {' を開き、用途がわかる名前（例: 仕事用 Mac）を付けて発行します。'}
                  </P>
                  <Note label="一度きり">
                    <P>
                      {
                        'トークンの平文が表示されるのは発行した直後の一度だけです。画面を離れると二度と出せません。控えそこねたら、古いものを失効させて発行し直してください。'
                      }
                    </P>
                  </Note>
                </>
              ),
            },
            {
              title: 'AI クライアントに登録する',
              body: (
                <P>
                  {'使っているクライアントのタブで「トークンを貼る」を選び、内容を貼り付けます。'}
                  <Code>YOUR_TOKEN</Code>
                  {' は 1 で発行したトークンに置き換えてください。'}
                </P>
              ),
            },
          ]}
        />
      </HelpSection>

      <HelpSection heading="クライアント別の設定">
        <P>
          {
            '下は実際の設定画面と同じものです。「ブラウザで許可」と「トークンを貼る」を切り替えられます。トークンの差し込みまで行うなら、発行した直後に '
          }
          <Screen to="/settings/mcp">MCP接続</Screen>
          {' から進めるほうが確実です。'}
        </P>
        <ClientSetup endpoint={endpoint} token={null} />
      </HelpSection>

      <HelpSection heading="つながらないとき">
        <Terms
          rows={[
            {
              term: 'ブラウザが開かない',
              value:
                'そのクライアントが OAuth に対応していない可能性があります。「トークンを貼る」に切り替えてください。',
            },
            {
              term: '許可したのに繋がらなくなった',
              value: (
                <>
                  {
                    '「接続済みアプリ」から解除されていないかを確認してください。解除すると、その場で繋がらなくなります。もう一度繋ぐには、クライアント側で接続し直します。'
                  }
                </>
              ),
            },
            {
              term: '認証に失敗する',
              value: (
                <>
                  {'トークンで繋いでいる場合は '}
                  <Code>Authorization: Bearer</Code>
                  {
                    ' の形になっているかを確認してください。失効させたトークンは復活しないので、分からなくなったら発行し直すのが早いです。'
                  }
                </>
              ),
            },
            {
              term: '接続はできるが断られる',
              value:
                'トライアル期間が終わっているか、テナントが停止されている可能性があります。管理者に確認してください。',
            },
            {
              term: 'プロジェクトが 1 件も出ない',
              value:
                'まだプロジェクトが登録されていません。管理者に登録を依頼してください（アサインが無くても、登録さえあれば一覧には出ます）。',
            },
            {
              term: '設定を書き換えても反映されない',
              value:
                'クライアントによっては再起動が要ります。Claude Desktop は再起動してください。',
            },
          ]}
        />
        <Bullets
          items={[
            <>
              {'端末を無くしたときは '}
              <Screen to="/settings/mcp">MCP接続</Screen>
              {' から、その接続だけを解除（またはトークンだけを失効）できます。'}
            </>,
            <>
              {'記録のされ方は '}
              <SeeAlso slug="logging-time">工数の記録</SeeAlso>
              {' にあります。'}
            </>,
          ]}
        />
      </HelpSection>
    </>
  );
}
